import PropTypes from 'prop-types'
import RichText from '../../../RichText'
import classNames from 'classnames'
import { generateElementId } from '../../../../../common/helpers'
import PrimaryButton from 'src/components/shared/PrimaryButton'
import { useLeadId } from '../../../../../common/lead-id'
import PrimaryLink from '../../../BrandPages/PrimaryLink'
import React, { useMemo } from 'react'
import { useRouter } from 'next/router'
import { buildQueryString } from '../../../../../common/utils'

const BrandSection = ({
  tagline,
  title,
  description,
  childClassName,
  wrapperClassName,
  button,
  hasLink,
  link,
  isLeadId,
  children,
  onClick,
}) => {
  const leadId = useLeadId()
  const { query: baseQuery } = useRouter()

  let query = useMemo(
    () => buildQueryString(baseQuery, leadId),
    [baseQuery, leadId]
  )

  let url = button?.href
  if (isLeadId && leadId) {
    url = `${url}?${query}`
  }

  const id = useMemo(() => generateElementId(title), [title])

  return (
    <div
      className={classNames(
        'gap-x-primary grid grid-cols-12 text-center',
        wrapperClassName
      )}
    >
      <div
        id={`brand-section-${id}`}
        className="col-span-full flex flex-col px-4 lg:col-span-8 lg:col-start-3 lg:px-0"
      >
        {tagline ? (
          <h3
            id={generateElementId(tagline)}
            className="text-center text-15 font-medium leading-5 text-blue-base"
          >
            {tagline}
          </h3>
        ) : null}
        <h2
          id={`h2_${generateElementId(title)}`}
          className="whitespace-normal p-4 text-center text-2.5xl font-bold leading-10 text-black-base lg:whitespace-pre-line lg:px-0 lg:py-5 lg:text-37 lg:leading-52"
        >
          {title}
        </h2>
        <RichText
          contentData={description}
          className="text-15 leading-6.5 text-black-base-light"
        />
      </div>
      {hasLink ? (
        <div className="col-span-full mb-0">
          <PrimaryLink
            id={id}
            title={title}
            name={link.name}
            href={link.href}
            onClick={onClick}
            hasArrowIcon={link.hasArrowIcon}
          />
        </div>
      ) : null}
      {button ? (
        <div
          className={classNames(
            'col-span-full pb-9 pt-4 lg:pb-16 lg:pt-5',
            { 'flex justify-center': button.showInMobile },
            { 'hidden justify-center lg:flex': !button.showInMobile }
          )}
          data-testid="primary btn"
        >
          <PrimaryButton
            id={`button_${generateElementId(title)}`}
            sectionTitle={title}
            title={button.name}
            isLeadCta={button?.isLeadCta}
            href={url}
            className="bg-red-950 px-4 py-3 text-white"
          />
        </div>
      ) : null}
      {children ? (
        <div className={classNames('col-span-full', childClassName)}>
          {children}
        </div>
      ) : null}
    </div>
  )
}

BrandSection.defaultProps = {
  childClassName: '',
  descriptionClassname: '',
  children: null,
  description: {},
  link: {},
  isLeadId: false,
  hasLink: false,
  wrapperClassName: '',
  onClick: () => {},
}

BrandSection.propTypes = {
  tagline: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.object,
  button: PropTypes.object,
  link: PropTypes.object,
  childClassName: PropTypes.string,
  children: PropTypes.node,
  isLeadId: PropTypes.bool,
  hasLink: PropTypes.bool,
  wrapperClassName: PropTypes.string,
  onClick: PropTypes.func,
}

export default BrandSection
